import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronRight, Clock, User, CalendarDays, List, Facebook, Twitter, Link2,
  ArrowLeft, ArrowRight, ChevronDown,
} from 'lucide-react';
import AcademyNav from '../../components/academy/AcademyNav';
import ContentBlock from '../../components/academy/ContentBlock';
import ArticleCard from '../../components/academy/ArticleCard';
import CtaBand from '../../components/academy/CtaBand';
import Seo from '../../components/academy/Seo';
import BrandHero from '../../components/BrandHero';
import { getArticle, CATEGORY_META, ACCENTS } from '../../lib/academyApi';

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-800 rounded-2xl overflow-hidden bg-[#161618]">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid="faq-toggle"
      >
        <span className="font-semibold text-white">{q}</span>
        <ChevronDown className={`w-5 h-5 shrink-0 text-orange-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-5 text-zinc-300 leading-7">{a}</p>}
    </div>
  );
};

const AcademyArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState('');
  const bodyRef = useRef(null);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    getArticle(slug).then(setArticle).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const el = bodyRef.current;
      if (!el) return;
      const top = el.offsetTop;
      const height = el.offsetHeight - window.innerHeight;
      const scrolled = window.scrollY - top;
      const pct = Math.min(100, Math.max(0, (scrolled / Math.max(1, height)) * 100));
      setProgress(pct);
      // active toc heading
      const headings = el.querySelectorAll('h2[id]');
      let current = '';
      headings.forEach((h) => {
        if (h.getBoundingClientRect().top < 160) current = h.id;
      });
      setActiveId(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [article]);

  if (notFound) {
    return (
      <div className="min-h-screen" style={{ background: '#0e0e10' }}>
        <AcademyNav />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Article not found</h1>
          <p className="text-zinc-400 mt-3">The article you're looking for doesn't exist or was moved.</p>
          <Link to="/academy" className="inline-block mt-6 text-orange-400 font-semibold">← Back to Academy</Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen" style={{ background: '#0e0e10' }}>
        <AcademyNav />
        <p className="text-zinc-500 text-center py-32">Loading…</p>
      </div>
    );
  }

  const meta = CATEGORY_META[article.category] || { name: article.category, accent: 'orange' };
  const accent = ACCENTS[meta.accent] || ACCENTS.orange;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const share = (network) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(article.title);
    if (network === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}`, '_blank', 'noopener');
    if (network === 'twitter') window.open(`https://twitter.com/intent/tweet?url=${u}&text=${t}`, '_blank', 'noopener');
    if (network === 'copy') {
      navigator.clipboard?.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 60%, #0e0e10 100%)' }}>
      <Seo
        title={article.seoTitle || article.title}
        description={article.metaDescription}
        path={`/academy/${article.slug}`}
        image={article.heroImage}
        type="article"
        article={{ title: article.title, author: article.author }}
        breadcrumbs={[
          { name: 'Academy', path: '/academy' },
          { name: meta.name, path: `/academy/category/${article.category}` },
          { name: article.title, path: `/academy/${article.slug}` },
        ]}
        faq={article.faq || []}
      />
      <BrandHero />
      <AcademyNav />

      {/* Reading progress */}
      <div className="sticky top-16 z-40 h-0.5 bg-transparent">
        <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-[width] duration-150" style={{ width: `${progress}%` }} data-testid="reading-progress" />
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800/60">
        <img src={article.heroImage} alt={article.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e10] via-[#0e0e10]/85 to-[#0e0e10]/60" />
        <div className="relative max-w-4xl mx-auto px-6 pt-12 pb-14 md:pt-16 md:pb-20">
          <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400 mb-5">
            <Link to="/academy" className="hover:text-white">Academy</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to={`/academy/category/${article.category}`} className="hover:text-white">{meta.name}</Link>
          </div>
          <span className={`inline-block text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full border ${accent.chip}`}>{meta.name}</span>
          <h1 className="mt-4 text-4xl md:text-5xl font-extrabold text-white leading-[1.08] tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {article.title}
          </h1>
          <p className="mt-4 text-lg text-zinc-300 leading-relaxed max-w-2xl">{article.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-zinc-400">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {article.author}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {article.readingTime} min read</span>
            <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> Updated June 2026</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-12 lg:py-16">
        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-12">
          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-500 mb-4">
                <List className="w-4 h-4" /> On this page
              </p>
              <nav className="space-y-1 border-l border-zinc-800">
                {(article.tableOfContents || []).map((t) => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    className={`block pl-4 -ml-px border-l py-1.5 text-sm transition-colors ${activeId === t.id ? 'border-orange-500 text-orange-400 font-medium' : 'border-transparent text-zinc-400 hover:text-white'}`}
                    data-testid={`toc-${t.id}`}
                  >
                    {t.label}
                  </a>
                ))}
              </nav>

              <div className="mt-8 flex items-center gap-2">
                <span className="text-xs text-zinc-500">Share</span>
                <button onClick={() => share('facebook')} className="w-8 h-8 grid place-items-center rounded-full bg-white/5 text-zinc-300 hover:bg-orange-500 hover:text-white transition-colors" data-testid="share-facebook"><Facebook className="w-4 h-4" /></button>
                <button onClick={() => share('twitter')} className="w-8 h-8 grid place-items-center rounded-full bg-white/5 text-zinc-300 hover:bg-orange-500 hover:text-white transition-colors" data-testid="share-twitter"><Twitter className="w-4 h-4" /></button>
                <button onClick={() => share('copy')} className="w-8 h-8 grid place-items-center rounded-full bg-white/5 text-zinc-300 hover:bg-orange-500 hover:text-white transition-colors" data-testid="share-copy"><Link2 className="w-4 h-4" /></button>
              </div>
            </div>
          </aside>

          {/* Article content */}
          <article ref={bodyRef} className="min-w-0">
            <div className="max-w-3xl">
              {article.content.map((b, i) => (
                <ContentBlock key={i} block={b} />
              ))}

              {/* FAQ */}
              {article.faq?.length > 0 && (
                <section className="mt-16">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>Frequently Asked Questions</h2>
                  <div className="space-y-3">
                    {article.faq.map((f, i) => <FaqItem key={i} {...f} />)}
                  </div>
                </section>
              )}

              {/* Mobile share */}
              <div className="mt-12 flex items-center gap-2 lg:hidden">
                <span className="text-xs text-zinc-500">Share</span>
                <button onClick={() => share('facebook')} className="w-9 h-9 grid place-items-center rounded-full bg-white/5 text-zinc-300 hover:bg-orange-500 hover:text-white transition-colors"><Facebook className="w-4 h-4" /></button>
                <button onClick={() => share('twitter')} className="w-9 h-9 grid place-items-center rounded-full bg-white/5 text-zinc-300 hover:bg-orange-500 hover:text-white transition-colors"><Twitter className="w-4 h-4" /></button>
                <button onClick={() => share('copy')} className="w-9 h-9 grid place-items-center rounded-full bg-white/5 text-zinc-300 hover:bg-orange-500 hover:text-white transition-colors"><Link2 className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Prev / Next */}
            <div className="mt-14 grid sm:grid-cols-2 gap-4 max-w-3xl">
              {article.prev ? (
                <button onClick={() => navigate(`/academy/${article.prev.slug}`)} className="group text-left rounded-2xl border border-zinc-800 bg-[#161618] p-5 hover:border-zinc-700 transition-colors" data-testid="prev-article">
                  <span className="flex items-center gap-1 text-xs text-zinc-500 mb-1"><ArrowLeft className="w-3.5 h-3.5" /> Previous</span>
                  <span className="font-semibold text-white group-hover:text-orange-400 transition-colors line-clamp-2">{article.prev.title}</span>
                </button>
              ) : <div className="hidden sm:block" />}
              {article.next && (
                <button onClick={() => navigate(`/academy/${article.next.slug}`)} className="group text-right rounded-2xl border border-zinc-800 bg-[#161618] p-5 hover:border-zinc-700 transition-colors" data-testid="next-article">
                  <span className="flex items-center justify-end gap-1 text-xs text-zinc-500 mb-1">Next <ArrowRight className="w-3.5 h-3.5" /></span>
                  <span className="font-semibold text-white group-hover:text-orange-400 transition-colors line-clamp-2">{article.next.title}</span>
                </button>
              )}
            </div>
          </article>
        </div>

        {/* Related */}
        {article.related?.length > 0 && (
          <section className="mt-20">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {article.related.map((a) => <ArticleCard key={a.slug} article={a} />)}
            </div>
          </section>
        )}

        <div className="mt-16">
          <CtaBand />
        </div>
      </div>
    </div>
  );
};

export default AcademyArticle;
